import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Search, Fingerprint, GitCompare, Gavel, ArrowUpRight, Sparkles, Droplets } from "lucide-react";

const CHECK_STEPS = [
  { n: "01", title: "Source", icon: Search, desc: "We consider where the frame came from and any supporting information available with it." },
  { n: "02", title: "Identify", icon: Fingerprint, desc: "Brand markings, model numbers, colour codes, dimensions and other identifying details are checked where available." },
  { n: "03", title: "Inspect", icon: ShieldCheck, desc: "We examine construction, finish, hinges, logos, engraving, printing, hardware and other brand-specific details." },
  { n: "04", title: "Compare", icon: GitCompare, desc: "Where necessary, identifying details are compared with reliable reference information available for the brand and model." },
  { n: "05", title: "Decide", icon: Gavel, desc: "If we aren't comfortable representing the frame as genuine, we don't sell it." },
];

function Reveal({ children, delay = 0, className = "", ...rest }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export default function Authenticity() {
  return (
    <div data-testid="authenticity-page">
      {/* HERO */}
      <section className="bg-charcoal text-white relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-1 bg-[#09B8DF]" />
        <div className="container-lookers py-20 sm:py-28 relative z-10 max-w-3xl">
          <Reveal>
            <div className="text-[#09B8DF] text-xs font-heading font-bold uppercase tracking-[0.25em] mb-5" data-testid="authenticity-eyebrow">
              The Lookers Authenticity Guarantee
            </div>
            <h1 className="font-heading font-extrabold text-5xl sm:text-6xl lg:text-7xl tracking-tight mb-6">
              Genuine. Always.
            </h1>
            <p className="text-lg text-white/70 leading-relaxed mb-6 max-w-2xl">
              Preloved shouldn't mean uncertain. Every designer frame offered by Lookers goes through an
              authenticity and inspection process before it is listed for sale. If we cannot satisfy ourselves
              that a frame is genuine, we don't sell it.
            </p>
            <p className="font-heading font-bold text-base sm:text-lg text-white mb-10">
              No replicas. No imitations. No knowingly counterfeit frames.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/shop" className="inline-flex items-center gap-2 bg-[#09B8DF] hover:bg-teal-500 text-white font-heading font-bold text-xs uppercase tracking-wider px-7 py-4 rounded-full transition-colors" data-testid="authenticity-shop-cta">
                Shop Genuine Designer Frames <ArrowUpRight size={16} />
              </Link>
              <a href="#the-lookers-check" className="inline-flex items-center gap-2 border border-white/25 hover:border-white text-white font-heading font-bold text-xs uppercase tracking-wider px-7 py-4 rounded-full transition-colors" data-testid="authenticity-how-cta">
                How We Check Our Frames
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OUR PROMISE */}
      <section className="container-lookers py-16 sm:py-24 max-w-3xl">
        <Reveal>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-neutral-900 tracking-tight mb-6">
            Our promise is simple.
          </h2>
          <div className="space-y-4 text-neutral-600 leading-relaxed">
            <p>When you buy a designer frame from Lookers, we want you to be confident about exactly what you're buying.</p>
            <p>
              Every frame is individually assessed before listing. We examine the identifying details available on the frame,
              its construction, branding, markings, model information, materials, hardware and overall consistency with the
              genuine product where this information can reasonably be verified.
            </p>
            <p className="font-heading font-bold text-neutral-900">If something doesn't add up, the frame isn't listed.</p>
          </div>
        </Reveal>
      </section>

      {/* THE LOOKERS CHECK */}
      <section id="the-lookers-check" className="bg-[#F8F8F9] py-16 sm:py-24 scroll-mt-24" data-testid="the-lookers-check">
        <div className="container-lookers">
          <Reveal className="mb-12 max-w-2xl">
            <div className="text-teal-600 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-2">Every Frame, The Same Standard</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight">The Lookers Check</h2>
          </Reveal>
          <div className="grid gap-4 sm:gap-5 md:grid-cols-5">
            {CHECK_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.07} className="bg-white border border-neutral-200/70 rounded-2xl p-6 flex flex-col" data-testid={`check-step-${s.n}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono font-extrabold text-2xl text-neutral-300">{s.n}</span>
                  <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center text-teal-600">
                    <s.icon size={18} strokeWidth={2} />
                  </div>
                </div>
                <h3 className="font-heading font-extrabold text-lg text-neutral-900 mb-2">{s.title}</h3>
                <p className="text-sm text-neutral-500 leading-relaxed">{s.desc}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-10 max-w-3xl">
            <div className="bg-white border border-neutral-200/70 rounded-2xl p-6 sm:p-8">
              <div className="text-teal-600 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-3">Two Different Things</div>
              <p className="text-sm text-neutral-600 leading-relaxed mb-2">
                We're careful to distinguish between a <strong className="text-neutral-900">Lookers authenticity check</strong> —
                our own independent assessment that every frame goes through — and <strong className="text-neutral-900">independent
                third-party authentication</strong> by an outside specialist, which is not automatically the same thing.
              </p>
              <p className="text-sm text-neutral-600 leading-relaxed">
                Where a frame has been independently authenticated, we'll say so on that product and identify how it was verified.
                Unless a product states otherwise, our authenticity check is performed independently of the original brand or manufacturer.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* AUTHENTICITY + CONDITION ARE DIFFERENT */}
      <section className="container-lookers py-16 sm:py-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <div className="text-teal-600 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-2">Authenticity ≠ Condition</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-neutral-900 tracking-tight mb-5">Genuine doesn't mean brand new.</h2>
            <div className="space-y-4 text-neutral-600 leading-relaxed">
              <p>Lookers sells preloved eyewear. A genuine designer frame may show signs of its previous life.</p>
              <p>
                Authenticity describes whether we believe the frame is genuine. Condition describes its physical state.
                They are two separate things.
              </p>
              <p>
                That's why every Lookers frame receives a separate Condition Rating, detailed photographs and condition notes before you buy.
              </p>
            </div>
            <Link to="/condition-guide" className="inline-flex items-center gap-1.5 mt-6 text-xs font-heading font-bold uppercase tracking-wide text-teal-700 hover:text-teal-800 transition-colors" data-testid="authenticity-condition-link">
              Learn About Our Condition Ratings <ArrowUpRight size={14} />
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-charcoal text-white rounded-3xl p-8 relative overflow-hidden">
              <div className="absolute inset-x-0 top-0 h-1 bg-[#09B8DF]" />
              <div className="flex items-start gap-4 mb-6">
                <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-[#09B8DF] shrink-0"><ShieldCheck size={20} /></div>
                <div>
                  <div className="font-heading font-bold text-sm mb-1">Authenticity Checked</div>
                  <p className="text-sm text-white/60">Our assessment of whether the frame is genuine.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 mb-6">
                <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-[#09B8DF] shrink-0"><Sparkles size={20} /></div>
                <div>
                  <div className="font-heading font-bold text-sm mb-1">Condition Graded</div>
                  <p className="text-sm text-white/60">A separate 1–10 rating with photos and written notes.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center text-[#09B8DF] shrink-0"><Droplets size={20} /></div>
                <div>
                  <div className="font-heading font-bold text-sm mb-1">Professionally Prepared</div>
                  <p className="text-sm text-white/60">Cleaned and serviced where applicable before listing.</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* EVERY FRAME IS DIFFERENT */}
      <section className="bg-[#F8F8F9] py-16 sm:py-24">
        <div className="container-lookers max-w-3xl text-center">
          <Reveal>
            <div className="text-teal-600 text-xs font-heading font-bold uppercase tracking-[0.2em] mb-2">One Frame. One History.</div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-neutral-900 tracking-tight mb-5">Every frame is different.</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              Most Lookers frames are individual preloved pieces rather than mass-produced inventory sitting in a warehouse.
            </p>
            <p className="text-neutral-600 leading-relaxed mb-8">
              That's why we photograph and describe the actual frame you are buying wherever practical — including the details
              that make it great and the signs of wear that make it preloved.
            </p>
            <div className="inline-block font-heading font-extrabold text-2xl sm:text-3xl text-neutral-900 tracking-tight border-y-2 border-[#09B8DF] py-4">
              The frame you see is the frame you receive.
            </div>
          </Reveal>
        </div>
      </section>

      {/* AUTHENTICITY CONCERN */}
      <section className="container-lookers py-16 sm:py-24 max-w-3xl">
        <Reveal>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-neutral-900 tracking-tight mb-5">Still have a concern?</h2>
          <div className="space-y-4 text-neutral-600 leading-relaxed">
            <p>
              If you ever have a genuine concern about the authenticity of a frame purchased from Lookers, contact us with your
              order number and details of your concern. We will review the matter and the information available for that frame.
            </p>
            <p>Where appropriate, Lookers may seek additional specialist or independent authentication as part of that review.</p>
            <p className="text-sm text-neutral-500">
              Nothing in the Lookers Authenticity Guarantee limits any rights you may have under the Australian Consumer Law.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-charcoal hover:bg-teal-600 text-white font-heading font-bold text-xs uppercase tracking-wider px-7 py-4 rounded-full transition-colors" data-testid="authenticity-contact-cta">
              Contact Lookers
            </Link>
            <Link to="/returns" className="inline-flex items-center gap-2 border border-neutral-200 hover:border-charcoal text-neutral-700 font-heading font-bold text-xs uppercase tracking-wider px-7 py-4 rounded-full transition-colors" data-testid="authenticity-returns-cta">
              Returns &amp; Refunds
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
